import { coursePathsQueryKeys } from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCoursePath } from '@/pages/admin/courses/studio/CourseStudio.hooks';
import { useCourseStudio } from '@/pages/admin/courses/studio/info/CourseStudioInfo.hooks';
import { useCoursePathnameUpdater } from '../CourseBox.hooks';
import { CourseBoxHeaderProps } from './CourseBoxHeader';
import CourseBoxHeaderForm from './CourseBoxHeaderForm';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';

export default function CourseBoxHeaderService({
  coursePath,
}: CourseBoxHeaderProps) {
  const { cacheManager } = useCoursePath();
  const queryClient = useQueryClient();
  const { handleUpdateCourse } = useCourseStudio();
  const { updateCoursePathname } = useCoursePathnameUpdater();

  const handleUpdate = async (coursePathUpdated: CoursePath) => {
    const updateCourse = await handleUpdateCourse(
      coursePath.course,
      coursePathUpdated.course,
    );

    return {
      ...coursePath,
      slug: updateCourse.coursePathSlug,
      course: updateCourse,
    };
  };

  const { mutate, isPending } = useMutation({
    mutationFn: handleUpdate,
    onSuccess: data => {
      cacheManager.merge(data);
      updateCoursePathname(data.course.coursePathSlug);
      queryClient.invalidateQueries({
        queryKey: coursePathsQueryKeys.list._def,
      });
    },
    onError: error => {
      alert.error(getErrorMessage(error));
    },
  });

  const onSubmit = (params: { coursePath: CoursePath; validated: boolean }) => {
    mutate(params.coursePath);
  };

  return (
    <CourseBoxHeaderForm
      coursePath={coursePath}
      isLoading={isPending}
      onSubmit={debounce(onSubmit, 1000)}
    />
  );
}
